import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, Linkedin, Instagram, Mail } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Work With Me', path: '/work-with-me' },
    { name: '90-Day Program', path: '/program' },
    { name: 'Blog', path: '/blog' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-orange-50/90 backdrop-blur-md shadow-sm py-4 border-b border-orange-100' : 'bg-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <Link to="/" className="text-2xl font-serif font-bold tracking-tight text-stone-900">
          Productive Folks<span className="text-brand-dark">.</span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-8">
          {navLinks.map((link) => (
            <Link 
              key={link.path} 
              to={link.path} 
              className={`text-sm font-medium tracking-wide hover:text-brand-dark transition-colors ${location.pathname === link.path ? 'text-stone-900 border-b border-stone-900' : 'text-stone-600'}`}
            >
              {link.name}
            </Link>
          ))}
          <Link to="/contact" className="bg-stone-900 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-stone-800 transition-colors flex items-center gap-2">
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button className="md:hidden text-stone-900" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-orange-50 border-b border-orange-200 py-8 px-6 flex flex-col space-y-4 shadow-lg">
          {navLinks.map((link) => (
            <Link 
              key={link.path} 
              to={link.path} 
              className="text-lg font-serif text-stone-800"
            >
              {link.name}
            </Link>
          ))}
          <Link to="/contact" className="bg-stone-900 text-white px-6 py-3 rounded-md text-center font-medium mt-4">
            Contact Us
          </Link>
        </div>
      )}
    </nav>
  );
};

const Footer: React.FC = () => {
  return (
    <footer className="bg-stone-900 text-orange-100 py-16 px-6">
      <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-12">
        <div className="col-span-1 md:col-span-2">
          <h3 className="text-3xl font-serif text-white mb-6">Productive Folks.</h3>
          <p className="max-w-md text-orange-200/80 leading-relaxed">
            Helping ambitious women transform their skills into a sustainable business that supports their lifestyle. Designed for female solopreneurs to go from hidden talent to high-impact brand.
          </p>
        </div>
        <div>
          <h4 className="text-white font-medium mb-4">Navigation</h4>
          <ul className="space-y-3">
            <li><Link to="/work-with-me" className="hover:text-white transition-colors">Work With Me</Link></li>
            <li><Link to="/program" className="hover:text-white transition-colors">90-Day Program</Link></li>
            <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-medium mb-4">Connect</h4>
          <div className="space-y-4">
            <div>
                <div className="flex items-center gap-2 text-white mb-2">
                    <Mail size={18} /> <span className="font-medium">Email</span>
                </div>
                <div className="pl-7 space-y-1">
                    <a href="mailto:productivefolks@gmail.com" className="block text-sm text-orange-200/80 hover:text-white transition-colors">productivefolks@gmail.com</a>
                </div>
            </div>
            
            <a href="https://www.linkedin.com/company/productive-folks/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-orange-200/80 hover:text-white transition-colors">
                <Linkedin size={18} />
                <span>Productive Folks</span>
            </a>
            
            <a href="https://instagram.com/productive_folks" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-orange-200/80 hover:text-white transition-colors">
                <Instagram size={18} />
                <span>productive_folks</span>
            </a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto border-t border-stone-800 mt-16 pt-8 flex flex-col md:flex-row justify-between text-sm text-orange-200/60">
        <p>&copy; {new Date().getFullYear()} Productive Folks. All rights reserved.</p>
        <div className="flex space-x-6 mt-4 md:mt-0">
          <a href="#" className="hover:text-white">Privacy Policy</a>
          <a href="#" className="hover:text-white">Terms of Service</a>
        </div>
      </div>
    </footer>
  );
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-orange-50">
      <Navbar />
      <main className="flex-grow pt-24 md:pt-28">
        {children}
      </main>
      <Footer />
    </div>
  );
};